import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useWorkspace } from "../../integrationQA/context/WorkspaceContext";
import {
  assignConversation, claimConversation, listAgents, listConversationsWithMeta, sendAgentMessage, setConversationPriority
} from "../../api/services/conversationService";
import EscalationBanner from "./EscalationBanner";
import AgentQueueList from "./AgentQueueList";
import UnassignedConversationView from "./UnassignedConversationView";
import ConversationTranscript from "./ConversationTranscript";
import InternalNotes from "./InternalNotes";
import ConversationTags from "./ConversationTags";
import ConversationStatus from "./ConversationStatus";
import VisitorDetailPanel from "../VisitorDetailPanel";
import LeadProfilePanel from "../LeadProfilePanel";
import "../visitor-detail.css";
import "./AgentWorkspace.base.css";

const POLL_MS = 3000;
const minutesSince = (iso) => Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));

/** Maps an API conversation onto the shape the workspace components were built around. */
function toUi(c, userId) {
  const visitorMessages = c.messages.filter((m) => m.from === "visitor");
  const lastVisible = [...c.messages].reverse().find((m) => m.from !== "system");
  const lastVisitor = visitorMessages[visitorMessages.length - 1];
  const assigned = !!c.assignedAgentId;
  return {
    _sig: `${JSON.stringify(c)}|${userId}|${Math.floor(Date.now() / 60000)}`,
    id: c.id,
    visitorName: c.customer,
    visitorEmail: c.visitorEmail || "",
    leadId: c.leadId || null,
    visitorMeta: `Visitor · ${c.channel}${c.visitorEmail ? ` · ${c.visitorEmail}` : ""}`,
    lastMessage: lastVisible ? lastVisible.text : "",
    waitMinutes: assigned || !lastVisitor ? 0 : minutesSince(lastVisitor.createdAt),
    priority: c.priority || "low",
    status: assigned ? "assigned" : "unassigned",
    conversationStatus: c.status === "Resolved" ? "closed" : c.status.toLowerCase(),
    assignedAgentId: c.assignedAgentId,
    assignedAgentName: c.assignedAgentName,
    assignedToMe: c.assignedAgentId === userId,
    messages: c.messages.map((m) => ({
      id: m.id, createdAt: m.createdAt, from: m.from, text: m.text,
    })),
    internalNotes: (c.notes || []).map((n) => ({ id: n.id, text: n.text, createdAt: new Date(n.createdAt).toLocaleString() })),
    tags: (c.tags || []).map((t) => ({ id: t.id, name: t.name })),
  };
}

const TABS = [
  { key: "inbox", label: "Inbox" },
  { key: "escalations", label: "Escalations" },
  { key: "details", label: "Customer & case details" }
];

export default function AgentWorkspace({ tab = "inbox", onTabChange = () => {} }) {
  const { userId } = useWorkspace();
  const [conversations, setConversations] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [notice, setNotice] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [waitAlert, setWaitAlert] = useState(5);

  // Reload from the server; keep object identity for unchanged conversations so child effects don't re-fire.
  const refresh = useCallback(async () => {
    try {
      const { items: list, meta } = await listConversationsWithMeta();
      if (meta.waitAlertMinutes) setWaitAlert(meta.waitAlertMinutes);
      setConversations((previous) => {
        const known = new Map(previous.map((c) => [c.id, c]));
        return list.map((raw) => {
          const next = toUi(raw, userId);
          const prior = known.get(next.id);
          return prior && prior._sig === next._sig ? prior : next;
        });
      });
      setSelectedId((current) => (current && list.some((c) => c.id === current) ? current : (list[0] ? list[0].id : null)));
      setLoadState("ready");
    } catch (error) {
      setNotice(error.message || "Unable to load conversations.");
      setLoadState((state) => (state === "ready" ? state : "error"));
    }
  }, [userId]);

  useEffect(() => {
    refresh();
    const timer = window.setInterval(refresh, POLL_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  useEffect(() => {
    listAgents().then(setAgents).catch(() => setAgents([]));
  }, []);

  /* ---- escalation data ---- */
  const escalations = useMemo(
    () =>
      conversations
        .filter((c) => c.status === "unassigned" && c.priority === "high" && c.conversationStatus !== "closed")
        .map((c) => ({
          id: c.id,
          visitorName: c.visitorName,
          reason: "Bot could not resolve",
          waitMinutes: c.waitMinutes,
          overdue: c.waitMinutes >= waitAlert,
        })),
    [conversations, waitAlert]
  );

  const escalated = useMemo(() => {
    const ids = new Set(escalations.map((e) => e.id));
    return conversations.filter((c) => c.conversationStatus !== "closed" && (ids.has(c.id) || c.conversationStatus === "escalated"));
  }, [conversations, escalations]);
  const openInInbox = (id) => { setSelectedId(id); onTabChange("inbox"); };

  /* ---- selected conversation ---- */
  const selectedConversation = conversations.find((c) => c.id === selectedId) || null;

  const run = async (action, failure) => {
    setNotice("");
    try { await action(); await refresh(); }
    catch (error) { setNotice(error.message || failure); await refresh(); }
  };

  const handleClaim = (id) => run(() => claimConversation(id), "Unable to claim this conversation.");
  const handleAssign = (id, agentId) => run(() => assignConversation(id, agentId), "Unable to assign this conversation.");
  const handleSendMessage = (id, text) => run(() => sendAgentMessage(id, text), "Unable to send the reply.");
  // Notes, tags and status are persisted by their own panels (services/*Api.js); just re-sync here.
  const handleAddInternalNote = () => refresh();
  const handleAddConversationTag = () => refresh();
  const handleRemoveConversationTag = () => refresh();
  const handleStatusChange = () => refresh();

  return (
    <div className="cb-workspace">

      {/* ================================
          ChatBizAI Branded Header
      ================================= */}

      <div className="cb-page-header">

        {/* ChatBizAI Branding */}
        <div className="cb-brand-section">

          <img
            src="/ChatBizAI_logo_transparent.png"
            alt="ChatBizAI"
            className="cb-logo"
          />

          <div className="cb-brand-divider"></div>

          <div className="cb-workspace-title">

            <h1>Agent Workspace</h1>

            <div className="cb-subtitle">
              Queue, claim and respond to live visitor conversations
            </div>

          </div>

        </div>

        {/* Agent Online Status */}
        <div className="cb-agent-status">

          <span className="cb-status-dot"></span>

          Online

        </div>

      </div>

      <div className="aw-tabs" role="tablist" aria-label="Agent Workspace sections">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={tab === t.key ? "active" : ""} onClick={() => onTabChange(t.key)}>
            {t.label}
            {t.key === "escalations" && escalated.length > 0 && <span className="aw-tab-count">{escalated.length}</span>}
          </button>
        ))}
      </div>

      {notice && <div className="alert alert-warning py-2 mx-3" role="alert">{notice}</div>}
      {loadState === "loading" && <div className="text-muted small px-3 pb-2" role="status">Loading conversations…</div>}
      {loadState === "error" && <div className="alert alert-danger py-2 mx-3" role="alert">Could not load conversations. <button type="button" className="btn btn-link btn-sm p-0" onClick={refresh}>Retry</button></div>}
      {loadState === "ready" && conversations.length === 0 && <div className="text-muted small px-3 pb-2" role="status">No conversations yet — open the Visitor Chat Widget and send a message to start one.</div>}

      {tab === "inbox" && (
        <div className="cb-workspace-grid">
          <AgentQueueList conversations={conversations} selectedId={selectedId} onSelect={setSelectedId} />
          <div className="cb-conversation-panel">
            <UnassignedConversationView
              conversation={selectedConversation}
              agents={agents}
              onClaim={handleClaim}
              onAssign={handleAssign}
              onSendMessage={handleSendMessage}
            />
            <ConversationTranscript conversationId={selectedConversation?.id} messages={selectedConversation?.messages || []} />
          </div>
        </div>
      )}

      {tab === "escalations" && (
        <div className="cb-workspace-grid">
          <div>
            <EscalationBanner
              escalations={escalations}
              tone="danger"
              onView={openInInbox}
              onDismiss={(id) => run(() => setConversationPriority(id, "medium"), "Unable to dismiss this escalation.")}
            />
            {escalations.length === 0 && <div className="aw-empty-tab">Nothing is waiting for a human right now.</div>}
            <p className="aw-alert-note">Waiting-time alert: {waitAlert} min (set under Settings → Business hours).</p>
          </div>
          <AgentQueueList conversations={escalated} selectedId={selectedId} onSelect={openInInbox} />
        </div>
      )}

      {tab === "details" && (
        <div className="aw-details">
          <div className="aw-details-pick">
            <label htmlFor="aw-details-conversation">Conversation</label>
            <select id="aw-details-conversation" className="form-select" value={selectedId || ""} onChange={(e) => setSelectedId(e.target.value)} disabled={conversations.length === 0}>
              {conversations.length === 0 && <option value="">No conversations</option>}
              {conversations.map((c) => <option key={c.id} value={c.id}>{c.visitorName} · {c.id}</option>)}
            </select>
          </div>
          {selectedConversation ? (
            <div className="aw-details-grid">
              <div>
                <VisitorDetailPanel conversation={selectedConversation} />
                <LeadProfilePanel conversation={selectedConversation} />
                <ConversationStatus conversationId={selectedConversation.id} currentStatus={selectedConversation.conversationStatus} onStatusChange={handleStatusChange} />
              </div>
              <div>
                <ConversationTags conversationId={selectedConversation.id} tags={selectedConversation.tags} onAddTag={handleAddConversationTag} onRemoveTag={handleRemoveConversationTag} />
                <InternalNotes conversationId={selectedConversation.id} notes={selectedConversation.internalNotes} onAddNote={handleAddInternalNote} />
              </div>
            </div>
          ) : <div className="aw-empty-tab">Pick a conversation in the Inbox to see its visitor details, status, tags and notes.</div>}
        </div>
      )}
    </div>
  );
}
